import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useBackButtonClose } from "@/hooks/use-back-button-close"

type DialogRootProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Root
> & {
  /**
   * When true (default), pressing the browser/system Back button while
   * the dialog is open closes the dialog instead of navigating the
   * underlying page. Set to false for dialogs that hold in-flight edits
   * and refuse to close on outside-click / Escape — the same intent
   * applies to the Back button.
   */
  closeOnBackButton?: boolean
}

const Dialog = ({
  open,
  defaultOpen,
  onOpenChange,
  closeOnBackButton = true,
  ...props
}: DialogRootProps) => {
  const isControlled = open !== undefined
  const [internalOpen, setInternalOpen] = React.useState<boolean>(
    defaultOpen ?? false,
  )
  const effectiveOpen = isControlled ? !!open : internalOpen
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )
  useBackButtonClose(effectiveOpen, () => handleOpenChange(false), {
    enabled: closeOnBackButton,
  })
  return (
    <DialogPrimitive.Root
      {...props}
      {...(isControlled ? { open } : { defaultOpen })}
      onOpenChange={handleOpenChange}
    />
  )
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/65 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * When true, the dialog renders fullscreen below the `sm` breakpoint
   * (≤640 px) and reverts to the centered desktop layout at `sm` and above.
   * Use for multi-strip authoring dialogs (Style Product on Model, Expand
   * Headshot, Precise Edit / Insert, Create Model, etc.) so each strip
   * stacks vertically with a sticky footer for actions.
   */
  mobileFullscreen?: boolean
  /**
   * Skip rendering the built-in pointer-blocking `DialogOverlay`. Callers
   * using `modal={false}` on the parent `<Dialog>` should set this to true
   * and render their own visual backdrop (typically with
   * `pointer-events-none`) so interaction with the page behind the dialog
   * isn't blocked. Without this, the default overlay would still capture
   * clicks even though Radix's modal focus trap is disabled.
   */
  hideOverlay?: boolean
  /**
   * Skip rendering the built-in top-right close (X) button. Use for modal
   * dialogs where dismissal must be limited to explicit Cancel / confirm
   * actions (e.g. deliberate-choice pickers that also block outside-click
   * and Escape dismissal).
   */
  hideClose?: boolean
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, mobileFullscreen, hideOverlay, hideClose, ...props }, ref) => (
  <DialogPortal>
    {hideOverlay ? null : <DialogOverlay />}
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Base sizing: cap at viewport width with comfortable margins so
        // dialogs that override max-w-* still cannot push past the screen.
        "fixed z-50 grid gap-4 border border-border bg-card shadow-2xl duration-200",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        mobileFullscreen
          ? [
              // Mobile: full-screen sheet that fills the viewport edge-to-edge.
              // The dialog itself becomes the scroll container, with header/
              // footer pinned via `position: sticky` inside the content tree.
              "inset-0 left-0 top-0 w-full h-full max-w-none max-h-none translate-x-0 translate-y-0 p-4 overflow-y-auto rounded-none",
              "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
              // Desktop: revert to centered layout.
              "sm:fixed sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:w-full sm:max-w-lg sm:h-auto sm:max-h-[90vh] sm:p-6 sm:rounded-lg",
            ]
          : [
              // Default centered layout. Caps width to viewport so any
              // override (max-w-6xl, w-[1200px], etc.) still respects the
              // screen, and caps height so the dialog itself scrolls instead
              // of pushing content off-screen.
              "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]",
              "w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:max-w-lg",
              "max-h-[calc(100dvh-2rem)] sm:max-h-[90vh]",
              "p-4 sm:p-6 rounded-lg",
              "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            ],
        className
      )}
      {...props}
    >
      {children}
      {hideClose ? null : (
        <DialogPrimitive.Close
          className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground sm:h-8 sm:w-8 sm:right-4 sm:top-4"
          aria-label="Close"
        >
          <X className="h-5 w-5 sm:h-4 sm:w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      // Leave room for the absolute close button (44 px tap target on mobile).
      "pr-12 sm:pr-10",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Pin to the bottom of the dialog's scroll container at every breakpoint
      // so the primary action is always reachable in long forms / multi-strip
      // flows. The card-coloured background prevents content from showing
      // through. Negative margins / padding match DialogContent's p-4 (mobile)
      // and sm:p-6 (desktop) so the footer spans edge-to-edge of the dialog.
      "sticky bottom-0 z-10 bg-card border-t border-border",
      "-mx-4 -mb-4 px-4 py-3 sm:-mx-6 sm:-mb-6 sm:px-6 sm:py-3",
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
